/**
 * Krok 3: optimistic UI dla akcji w skrzynce odbiorczej.
 *
 * Wszystkie mutacje w jednym miejscu — onMutate robi snapshot + setQueriesData,
 * onError przywraca snapshot, onSettled inwaliduje statystyki/folder counts
 * (te liczy backend, nie da się ich łatwo zaktualizować lokalnie). Zysk:
 * lista reaguje na klik <50ms, wszystkie błędy backendu/IMAP są transparent
 * dla użytkownika (rollback + toast).
 */

import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { messagesApi } from '../lib/api';

interface ListMessage {
  id: string;
  conversationRootId?: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived?: boolean;
  isDeleted?: boolean;
  imapFolderId?: string | null;
  imapFolder?: { specialUse?: string | null } | null;
  conversationStatus?: string;
}

interface PageShape {
  data: ListMessage[];
  pagination?: unknown;
  [k: string]: unknown;
}

interface InfiniteData<T> {
  pages: T[];
  pageParams: unknown[];
}

type Snapshot = Array<[QueryKey, InfiniteData<PageShape> | undefined]>;

type ListMutator = (msg: ListMessage, queryKey: QueryKey) => ListMessage | null; // null = usuń z listy

function transformAllMessageQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  ids: string[],
  mutate: ListMutator,
  wholeConversation = false,
): Snapshot {
  const idSet = new Set(ids);
  const snapshot: Snapshot = [];
  const queries = queryClient.getQueriesData<InfiniteData<PageShape>>({ queryKey: ['messages'] });
  if (wholeConversation) {
    const rows = queries.flatMap(([, data]) => data?.pages.flatMap(page => page.data) || []);
    const roots = new Set(rows.filter(row => idSet.has(row.id)).map(row => row.conversationRootId || row.id));
    for (const row of rows) if (roots.has(row.conversationRootId || row.id)) idSet.add(row.id);
  }
  for (const [key, data] of queries) {
    snapshot.push([key, data]);
    if (!data) continue;
    const newPages = data.pages.map((page) => ({
      ...page,
      data: page.data
        .map((m) => (idSet.has(m.id) ? mutate(m, key) : m))
        .filter((m): m is ListMessage => m !== null),
    }));
    queryClient.setQueryData(key, { ...data, pages: newPages });
  }
  return snapshot;
}

function rollback(queryClient: ReturnType<typeof useQueryClient>, snapshot: Snapshot): void {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}

/**
 * Znajdź mail w cache ['messages'] po id — potrzebne do optimistic update
 * drzewa (wiemy folderId + czy mail był już przeczytany, żeby nie dublować
 * odejmowania unreadCount).
 */
function findMessageInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  messageId: string,
): ListMessage | null {
  const queries = queryClient.getQueriesData<InfiniteData<PageShape>>({ queryKey: ['messages'] });
  for (const [, data] of queries) {
    if (!data) continue;
    for (const page of data.pages) {
      const m = page.data.find((x) => x.id === messageId);
      if (m) return m;
    }
  }
  return null;
}

function findMessageFolderIdInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  messageId: string,
): string | null {
  return findMessageInCache(queryClient, messageId)?.imapFolderId ?? null;
}

/**
 * Skoryguj unreadCount dla konkretnego folderu w cache ['imap-folders-tree'] —
 * optimistic delta (+1 / -1) zamiast czekania na backend refetch. Sidebar
 * renderuje się z tych wartości i pokazuje użytkownikowi zmianę natychmiast.
 * Po zakończeniu mutacji i tak idzie refetchQueries — jeśli nasza predykcja
 * była zła (edge case race z inną sesją), ostateczna wartość zastąpi naszą.
 */
function adjustTreeUnreadCount(
  queryClient: ReturnType<typeof useQueryClient>,
  folderId: string,
  delta: number,
): void {
  const treeQueries = queryClient.getQueriesData<{
    data?: Array<{ folders: Array<{ id: string; unreadCount: number; children?: unknown[] }> }>;
  }>({ queryKey: ['imap-folders-tree'] });
  for (const [key, data] of treeQueries) {
    if (!data || !Array.isArray(data.data)) continue;
    // Prosta rekurencja: walk po drzewie i adjust na pasującym id.
    type Node = { id: string; unreadCount: number; children: Node[] };
    const walk = (nodes: Node[]): Node[] =>
      nodes.map((n) =>
        n.id === folderId
          ? { ...n, unreadCount: Math.max(0, n.unreadCount + delta), children: walk(n.children || []) }
          : { ...n, children: walk(n.children || []) },
      );
    const nextData = {
      ...data,
      data: data.data.map((cfg) => ({ ...cfg, folders: walk(cfg.folders as Node[]) })),
    };
    queryClient.setQueryData(key, nextData);
  }
}

function invalidateStats(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['navigation-messages'] });
  // messages-stats: liczniki aggregatowe (Odebrane/Kosz/Spam/...) z /messages/stats
  // imap-folders-tree: drzewo IMAP z per-folder unreadCount — bez tego sidebar
  // w sekcjach "Wszystkie foldery" i system folders pokazuje nieaktualne liczby
  // aż do refreshu strony. refetchType: 'all' wymusza refetch nawet inactive
  // queries — bez tego sidebar licznik Kosza/Archiwum czasem zostawał z zamrożoną
  // wartością mimo zmiany isRead na mailu (user musiał F5).
  queryClient.invalidateQueries({ queryKey: ['messages-stats'], refetchType: 'all' });
  queryClient.invalidateQueries({ queryKey: ['imap-folders-tree'], refetchType: 'all' });
}

interface ToastFn {
  (msg: string, kind?: 'info' | 'error'): void;
}

/**
 * Opcjonalny callback wywoływany po każdej mutacji — przydatny dla state'a
 * który nie jest w React Query (np. `stats` przechowywany lokalnie w parencie
 * przez setState). Bez tego licznik "Odebrane" w sidebarze nie odświeża się
 * po oznaczeniu maila jako przeczytany, bo invalidateQueries('messages-stats')
 * nie wyzwala loadStats() skoro stats nie jest query.
 */
type PostMutateCallback = () => void;

export function useInboxMutations(
  showToast: ToastFn,
  onPostMutate?: PostMutateCallback,
  storeId?: string,
) {
  const queryClient = useQueryClient();

  const refreshStats = () => {
    invalidateStats(queryClient);
    onPostMutate?.();
  };

  const trashMessage = useMutation({
    mutationFn: (id: string) => messagesApi.deleteMessage(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      const snapshot = transformAllMessageQueries(queryClient, [id], () => null);
      return { snapshot };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      // Pokaż konkretny powód błędu z backendu (jeśli backend wrócił treść).
      // Wcześniej użytkownik widział tylko "Nie udało się usunąć wiadomości"
      // i nie wiedział co zrobić — Drafty AI flow pokazywał ten sam komunikat
      // dla 404, 500, network error.
      const apiErr = err as { response?: { data?: { error?: string }; status?: number }; message?: string };
      const backendMessage = apiErr?.response?.data?.error;
      const status = apiErr?.response?.status;
      const detail = backendMessage || apiErr?.message || (status ? `błąd ${status}` : 'błąd sieci');
      console.error('[trashMessage] failed:', { status, backendMessage, err });
      showToast(`Nie udało się usunąć wiadomości: ${detail}`, 'error');
    },
    onSettled: refreshStats,
  });

  const toggleRead = useMutation({
    mutationFn: ({ id, isRead }: { id: string; isRead: boolean }) =>
      messagesApi.toggleRead(id, isRead),
    onMutate: async ({ id, isRead }) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      const snapshot = transformAllMessageQueries(queryClient, [id], (m) => ({ ...m, isRead }));

      // Optimistic update drzewa IMAP — zmniejsz/zwiększ unreadCount dla
      // folderu, w którym leży mail. Bez tego licznik w sidebarze (Kosz,
      // Archiwum, custom foldery) zostawał z poprzednią wartością aż do
      // zakończenia refetch po backendzie (1-3s + cache). User widzi teraz
      // natychmiastową zmianę, spójnie z INBOX-em.
      const msgFolderId = findMessageFolderIdInCache(queryClient, id);
      if (msgFolderId) {
        adjustTreeUnreadCount(queryClient, msgFolderId, isRead ? -1 : +1);
      }

      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      showToast('Błąd zmiany statusu przeczytania', 'error');
    },
    onSettled: refreshStats,
  });

  const toggleStar = useMutation({
    mutationFn: ({ id }: { id: string; isStarred: boolean; removeFrom?: { folderFilter: string; imapFolderId: string | null } }) => messagesApi.toggleStar(id),
    onMutate: async ({ id, isStarred, removeFrom }) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      const snapshot = transformAllMessageQueries(queryClient, [id], (m, queryKey) => {
        const queryFolder = queryKey[2];
        const queryImapFolderId = queryKey[3] ?? null;
        const clearsResolved = isStarred && m.conversationStatus === 'resolved';
        const isSourceQuery = removeFrom
          && queryFolder === removeFrom.folderFilter
          && queryImapFolderId === removeFrom.imapFolderId;
        if (
          isSourceQuery
          || (!isStarred && queryFolder === 'starred')
          || (clearsResolved && queryFolder === 'resolved')
        ) return null;
        return {
          ...m,
          isStarred,
          ...(clearsResolved ? { conversationStatus: 'open' } : {}),
        };
      });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      showToast('Błąd zmiany gwiazdki', 'error');
    },
    onSettled: () => {
      refreshStats();
      // Widok "Wyróżnione" może mieć w cache pustą listę. Sam optimistic
      // update modyfikuje tylko rekordy już obecne w danym query, więc nie
      // potrafi dodać świeżo oznaczonej wiadomości do wcześniej pustego
      // widoku. Refetch wszystkich list domyka również opcjonalny MOVE do
      // folderu zmapowanego na serwerze IMAP.
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const archiveMessage = useMutation({
    mutationFn: (id: string) => messagesApi.archiveMessage(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      const snapshot = transformAllMessageQueries(queryClient, [id], () => null);
      return { snapshot };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      showToast('Błąd archiwizacji', 'error');
    },
    onSettled: refreshStats,
  });

  const moveToFolder = useMutation({
    mutationFn: ({ id, imapFolderId }: { id: string; imapFolderId: string }) =>
      messagesApi.moveToFolder(id, imapFolderId),
    onMutate: async ({ id, imapFolderId }) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      // Mail znika z aktualnego widoku — w jego nowym folderze pojawi się po invalidate.
      const snapshot = transformAllMessageQueries(queryClient, [id], (m) =>
        m.imapFolderId === imapFolderId ? { ...m, imapFolderId } : null,
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      showToast('Błąd przenoszenia wiadomości', 'error');
    },
    onSettled: () => {
      refreshStats();
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const bulkAction = useMutation({
    mutationFn: (data: { ids: string[]; action: string }) => {
      if (!storeId) throw new Error('Wybierz sklep przed wykonaniem akcji.');
      return messagesApi.bulkAction({ ...data, storeId });
    },
    onMutate: async ({ ids, action }) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      let mutator: ListMutator;
      switch (action) {
        case 'read': mutator = (m) => ({ ...m, isRead: true }); break;
        case 'unread': mutator = (m) => ({ ...m, isRead: false }); break;
        case 'star': mutator = (m, queryKey) => {
          const clearsResolved = m.conversationStatus === 'resolved';
          if (clearsResolved && queryKey[2] === 'resolved') return null;
          return {
            ...m,
            isStarred: true,
            ...(clearsResolved ? { conversationStatus: 'open' } : {}),
          };
        }; break;
        case 'unstar': mutator = (m) => ({ ...m, isStarred: false }); break;
        case 'trash':
        case 'archive':
        case 'resolve':
        case 'move_to_folder':
        case 'restore': // z widoku Kosza/Archive mail znika po restore (wraca do INBOX)
          mutator = () => null;
          break;
        default:
          mutator = (m) => m;
      }

      // Optimistic update drzewa IMAP dla masowego read/unread — bez tego
      // licznik nieprzeczytanych w sidebarze nie zmieniał się po "Zaznacz
      // wszystkie + Przeczytane", bo optimistic update był podpięty tylko pod
      // pojedynczy toggleRead. Dla każdego zaznaczonego maila (którego stan
      // faktycznie się zmienia) korygujemy unreadCount folderu w cache.
      const treeAdjustments: Array<{ folderId: string; delta: number }> = [];
      if (action === 'read' || action === 'unread') {
        const targetIsRead = action === 'read';
        for (const id of ids) {
          const msg = findMessageInCache(queryClient, id);
          if (msg?.imapFolderId && msg.isRead !== targetIsRead) {
            const delta = targetIsRead ? -1 : +1;
            adjustTreeUnreadCount(queryClient, msg.imapFolderId, delta);
            treeAdjustments.push({ folderId: msg.imapFolderId, delta });
          }
        }
      }

      const snapshot = transformAllMessageQueries(queryClient, ids, mutator, ['resolve', 'reopen', 'set_pending'].includes(action));
      return { snapshot, treeAdjustments };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      // Cofnij optimistic adjustments drzewa (odwrotny delta).
      if (ctx?.treeAdjustments) {
        for (const adj of ctx.treeAdjustments) {
          adjustTreeUnreadCount(queryClient, adj.folderId, -adj.delta);
        }
      }
      showToast('Błąd akcji masowej', 'error');
    },
    onSettled: () => {
      refreshStats();
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      messagesApi.changeStatus(id, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['messages'] });
      // Rozwiązane wyklucza gwiazdkę. Widoki Odebrane/Rozwiązane/Wyróżnione
      // oraz aktywny filtr statusu reagują od razu, bez czekania na refetch.
      const mutator = (m: ListMessage, queryKey: QueryKey) => {
        const queryFolder = queryKey[2];
        const queryStatus = queryKey[7];
        if (queryFolder === 'inbox' && status === 'resolved') return null;
        if (queryFolder === 'resolved' && status !== 'resolved') return null;
        if (typeof queryStatus === 'string' && queryStatus !== 'all' && queryStatus !== status) return null;
        if (status === 'resolved' && queryFolder === 'starred') return null;
        return {
          ...m,
          conversationStatus: status,
          ...(status === 'resolved' ? { isStarred: false } : {}),
        };
      };
      const snapshot = transformAllMessageQueries(queryClient, [id], mutator, true);
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) rollback(queryClient, ctx.snapshot);
      showToast('Błąd zmiany statusu', 'error');
    },
    onSettled: () => {
      refreshStats();
      // Invalidate — odświeża listy wszystkich folderów (w tym Rozwiązane
      // dociąga nową wiadomość jeśli user tam patrzy).
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  return {
    trashMessage,
    toggleRead,
    toggleStar,
    archiveMessage,
    moveToFolder,
    bulkAction,
    changeStatus,
  };
}
